import apiService from './api'
import { buildEndpoint, isCustomerServiceUser } from '../utils/apiUtils'
import { Order, OrderItem, ApiResponse } from '@/types'
import { parseOrderPromotion } from '../features/offers/orderPromotion'
import { parseServicePrice } from '../utils'
import brandManager from './brandManager'

export interface CashierOrdersParams {
  typeId?: number | string
  typesArr?: Array<number | string>
  statusesArr?: Array<number | string>
  branchId?: number | string
  paymentMethod?: string
  platform?: string
  status?: string
  statusId?: number | string
  dateFrom?: string
  dateTo?: string
  page?: number
  search?: string
}

export interface CashierOrdersResponse {
  orders: Order[]
  meta: {
    total: number
    perPage: number
    currentPage: number
    lastPage: number
  }
}

export interface CashierOrderResponse {
  id: string | number
  order_number?: string
  orderNumber?: string
  customer?: {
    id: string | number
    name: string
    phone: string
    email?: string
  }
  user?: {
    id: string | number
    name: string
    phone?: string
    email?: string
  }
  customer_name?: string
  customer_phone?: string
  phone?: string
  name?: string
  status?: string
  status_id?: number | string
  order_type?: string
  orderType?: string
  type_id?: number | string
  payment_method?: string
  paymentMethod?: string
  payment_status?: string
  paymentStatus?: string
  branch?: string | {
    id: string | number
    name: string
  }
  itemsCount?: number | string
  total_price?: string | number
  creator?: string | null
  items?: Array<{
    id: string | number
    product_id?: string | number
    product?: {
      id: string | number
      name: string
      name_en?: string
      price: number
    }
    meals?: Array<{
      id: string | number
      name: string
      name_en?: string
      image?: string
      additionals?: Array<{
        id: string | number
        name: string
        quantity?: number | null
        price: string | number
      }>
    }>
    quantity: number
    price: number | string
    total_price?: number
    total_unit_price?: number
    notes?: string
    modifiers?: Array<{
      id: string | number
      name: string
      price: number
    }>
    additionals?: Array<{
      id: string | number
      name: string
      quantity?: number | null
      price: string | number
    }>
  }>
  subtotal?: number
  sub_total?: number
  price_before_discount?: number | string
  tax_amount?: number
  taxAmount?: number
  tax_value?: number | string
  service_price?: number | string | null
  discount_amount?: number
  discountAmount?: number
  promotion?: {
    discount?: number | string | null
    cashback?: number | string | null
    set_item_price_savings?: number | string | null
    setItemPriceSavings?: number | string | null
  }
  total_amount?: number
  totalAmount?: number
  total?: number
  created_at?: string
  createdAt?: string
  created_by?: string | number
  createdBy?: string | number
  branch_id?: string | number
  branchId?: string | number
  table_id?: string | number
  tableId?: string | number
  table?: string | null | {
    id: string | number
    number: string
    name?: string
  }
  delivery_address?: string | {
    id: string
    address: string
    details?: string
    city?: string
    area?: string
  }
  deliveryAddress?: string | {
    id: string
    address: string
    details?: string
    city?: string
    area?: string
  }
  car_details?: {
    type?: string
    color?: string
    number?: string
  }
  carDetails?: {
    type?: string
    color?: string
    number?: string
  }
  platform?: string
  sync_status?: string
  syncStatus?: string
  notes?: string
  payments?: Array<{
    id: string | number
    amount: number
    method: string
    status: string
    transaction_id?: string
    transactionId?: string
  }>
  partial_payments?: any[]
  partialPayments?: any[]
}

/**
 * Map API response to Order type
 */
function mapOrderResponse(orderData: CashierOrderResponse): Order {
  // Map status with status_id - returns status and translation key
  const statusMapping = mapOrderStatus(orderData.status, orderData.status_id)
  
  // Map order type with type_id - returns type and translation key
  const orderTypeMapping = orderData.type_id 
    ? mapOrderType(orderData.type_id)
    : { type: 'takeaway' as Order['order_type'], typeKey: 'orders.type.takeaway' }
  
  // Use the mapped type
  const orderType = orderTypeMapping.type
  
  // Check if API returned translated labels (from statusLabel or orderTypeLabel fields)
  // If API provides labels, use them; otherwise use translation keys
  const statusLabel = (orderData as any).statusLabel || (orderData as any).status_label || statusMapping.statusKey
  const orderTypeLabel = (orderData as any).orderTypeLabel || (orderData as any).order_type_label || orderTypeMapping.typeKey
  
  // Parse total_price
  const totalPrice = orderData.total_price 
    ? (typeof orderData.total_price === 'string' ? parseFloat(orderData.total_price) : orderData.total_price)
    : 0
  
  // Parse branch (can be string or object)
  const branchName = typeof orderData.branch === 'string' 
    ? orderData.branch 
    : orderData.branch?.name || ''
  
  // Parse itemsCount
  const itemsCount = orderData.itemsCount 
    ? (typeof orderData.itemsCount === 'string' ? parseInt(orderData.itemsCount) : orderData.itemsCount)
    : (orderData.items?.length || 0)
  
  const order: Order = {
    id: orderData.id,
    order_number: orderData.order_number || orderData.orderNumber || String(orderData.id),
    customer_id: orderData.customer?.id || orderData.user?.id,
    customer: orderData.customer ? {
      id: String(orderData.customer.id),
      name: orderData.customer.name,
      phone: orderData.customer.phone,
      email: orderData.customer.email,
    } : (orderData.user ? {
      id: String(orderData.user.id),
      name: orderData.user.name,
      phone: orderData.user.phone || '',
      email: orderData.user.email,
    } : undefined),
    customerName: orderData.customer_name || orderData.customer?.name || orderData.user?.name || orderData.name,
    phoneNumber: orderData.customer_phone || orderData.customer?.phone || orderData.user?.phone || orderData.phone,
    items: (orderData.items || []).map((item): OrderItem => {
      // Extract product name from meals array if available, otherwise use product
      const meal = item.meals?.[0]
      const productName = meal?.name || item.product?.name || ''
      const productNameEn = meal?.name_en || item.product?.name_en || ''
      
      // Extract modifiers from meals additionals or item modifiers
      const additionals = meal?.additionals || item.additionals || []
      const modifiers = item.modifiers || additionals.map((add: any) => ({
        id: String(add.id),
        name: add.name,
        price: typeof add.price === 'string' ? parseFloat(add.price) : (add.price || 0),
      }))
      
      // Get price - try total_unit_price first, then total_price, then calculate
      const itemPrice = typeof item.price === 'string' ? parseFloat(item.price) : (item.price || 0)
      const totalUnitPrice = item.total_unit_price || item.total_price
      const totalPrice = totalUnitPrice 
        ? (typeof totalUnitPrice === 'string' ? parseFloat(totalUnitPrice) : totalUnitPrice)
        : (itemPrice * item.quantity)
      
      return {
        id: String(item.id),
        product_id: item.product_id || item.product?.id || meal?.id || '',
        product: {
          id: String(item.product_id || item.product?.id || meal?.id || ''),
          name: productName,
          name_en: productNameEn,
          price: itemPrice,
          category_id: '',
          is_available: true,
        },
        quantity: item.quantity,
        price: itemPrice,
        total_price: totalPrice,
        notes: item.notes,
        modifiers: modifiers.map(mod => ({
          id: String(mod.id),
          name: mod.name,
          price: typeof mod.price === 'number' ? mod.price : parseFloat(String(mod.price || 0)),
        })),
        // Store meals data for components that need it
        meals: item.meals,
      } as OrderItem & { meals?: any[] }
    }),
    order_type: orderType,
    status: statusMapping.status,
    subtotal: orderData.price_before_discount 
      ? (typeof orderData.price_before_discount === 'string' ? parseFloat(orderData.price_before_discount) : orderData.price_before_discount)
      : (orderData.subtotal || orderData.sub_total || 0),
    tax_amount: orderData.tax_value 
      ? (typeof orderData.tax_value === 'string' ? parseFloat(orderData.tax_value) : orderData.tax_value)
      : (orderData.tax_amount || orderData.taxAmount || 0),
    discount_amount: orderData.discount_amount || orderData.discountAmount || 0,
    service_price: parseServicePrice(orderData),
    promotion: parseOrderPromotion(orderData.promotion),
    total_amount: totalPrice || orderData.total_amount || orderData.totalAmount || orderData.total || 0,
    payment_status: mapPaymentStatus(orderData.payment_status || orderData.paymentStatus || 'pending'),
    payment_method: mapPaymentMethod(orderData.payment_method || orderData.paymentMethod),
    table_id: orderData.table_id || orderData.tableId,
    table: typeof orderData.table === 'string'
      ? (orderData.table.trim() ? {
          id: String(orderData.table_id || orderData.tableId || ''),
          number: '',
          name: orderData.table.trim(),
          capacity: 0,
          status: 'available',
          is_active: true,
        } : undefined)
      : orderData.table ? {
          id: String(orderData.table.id),
          number: orderData.table.number,
          name: orderData.table.name,
          capacity: 0,
          status: 'available',
          is_active: true,
        } : undefined,
    delivery_address: orderData.delivery_address || orderData.deliveryAddress ? {
      id: typeof (orderData.delivery_address || orderData.deliveryAddress) === 'string' 
        ? '' 
        : (orderData.delivery_address as any)?.id || (orderData.deliveryAddress as any)?.id || '',
      customer_id: orderData.customer?.id || '',
      name: '',
      address: typeof (orderData.delivery_address || orderData.deliveryAddress) === 'string'
        ? (orderData.delivery_address || orderData.deliveryAddress) as string
        : (orderData.delivery_address as any)?.address || (orderData.deliveryAddress as any)?.address || '',
      city: typeof (orderData.delivery_address || orderData.deliveryAddress) === 'object'
        ? ((orderData.delivery_address as any)?.city || (orderData.deliveryAddress as any)?.city || '')
        : '',
      coordinates: undefined,
      phone: orderData.customer?.phone,
      notes: typeof (orderData.delivery_address || orderData.deliveryAddress) === 'object'
        ? ((orderData.delivery_address as any)?.details || (orderData.deliveryAddress as any)?.details)
        : undefined,
      is_default: false,
    } : undefined,
    notes: orderData.notes,
    created_by: orderData.created_by || orderData.createdBy || '',
    branch_id: orderData.branch_id || orderData.branchId || '',
    branch: branchName ? {
      id: typeof orderData.branch === 'object' ? String(orderData.branch.id) : '',
      name: branchName,
      address: '',
      is_active: true,
    } : undefined,
    created_at: orderData.created_at || orderData.createdAt || new Date().toISOString(),
    platform: orderData.platform,
    syncStatus: orderData.sync_status || orderData.syncStatus,
    creator: orderData.creator || undefined,
    itemsCount: itemsCount,
    // Store translation keys or API-provided labels for display
    // Components will use these with i18n if they're keys, or display directly if they're labels
    statusLabel: statusLabel,
    orderTypeLabel: orderTypeLabel,
    // Also store translation keys for components that need them
    statusKey: statusMapping.statusKey,
    orderTypeKey: orderTypeMapping.typeKey,
  } as Order & { statusLabel?: string; orderTypeLabel?: string; statusKey?: string; orderTypeKey?: string }

  return order
}

/**
 * Map order type ID to status type (not translated - translation happens in components)
 * Returns the type key that can be used with i18n
 */
function mapOrderType(typeId?: number | string): { type: Order['order_type'], typeKey: string } {
  if (!typeId) {
    return { type: 'takeaway', typeKey: 'orders.type.takeaway' }
  }
  
  const typeIdNum = typeof typeId === 'string' ? parseInt(typeId) : typeId
  
  switch (typeIdNum) {
    case 1:
      return { type: 'delivery', typeKey: 'orders.type.delivery' }
    case 2:
      return { type: 'takeaway', typeKey: 'orders.type.takeaway' }
    case 3:
      return { type: 'dine_in', typeKey: 'orders.type.dine_in' }
    case 4:
      return { type: 'car_pickup', typeKey: 'orders.type.car_pickup' }
    default:
      return { type: 'takeaway', typeKey: 'orders.type.takeaway' }
  }
}

/**
 * Map order status ID to Order status type (not translated - translation happens in components)
 * Returns the status and translation key
 */
function mapOrderStatus(status?: string | number, statusId?: number | string): { status: Order['status'], statusKey: string } {
  // If status_id is provided, use it
  if (statusId !== undefined && statusId !== null) {
    const statusIdNum = typeof statusId === 'string' ? parseInt(statusId) : statusId
    
    switch (statusIdNum) {
      case 1:
      case 2:
      case 3:
        return { status: 'preparing', statusKey: 'orders.status.active' }
      case 4:
        return { status: 'completed', statusKey: 'orders.status.completed' }
      case 6:
      case 9:
        return { status: 'pending', statusKey: 'orders.status.pending' }
      default:
        return { status: 'pending', statusKey: 'orders.status.pending' }
    }
  }
  
  // Fallback to string status
  if (!status) {
    return { status: 'pending', statusKey: 'orders.status.pending' }
  }
  
  const statusLower = typeof status === 'string' ? status.toLowerCase() : String(status).toLowerCase()
  if (statusLower.includes('cancel')) {
    return { status: 'cancelled', statusKey: 'orders.status.cancelled' }
  }
  if (statusLower.includes('complete') || statusLower.includes('done') || statusLower === '4') {
    return { status: 'completed', statusKey: 'orders.status.completed' }
  }
  if (statusLower.includes('prepar') || statusLower === '1' || statusLower === '2' || statusLower === '3') {
    return { status: 'preparing', statusKey: 'orders.status.active' }
  }
  if (statusLower.includes('ready')) {
    return { status: 'ready', statusKey: 'orders.status.active' }
  }
  if (statusLower.includes('confirm')) {
    return { status: 'confirmed', statusKey: 'orders.status.active' }
  }
  if (statusLower.includes('active')) {
    return { status: 'preparing', statusKey: 'orders.status.active' }
  }
  if (statusLower === '6' || statusLower === '9') {
    return { status: 'pending', statusKey: 'orders.status.pending' }
  }
  return { status: 'pending', statusKey: 'orders.status.pending' }
}

function mapPaymentStatus(status?: string): Order['payment_status'] {
  if (!status) return 'pending'
  
  const statusLower = status.toLowerCase()
  if (statusLower.includes('paid') || statusLower.includes('success')) return 'paid'
  if (statusLower.includes('partial')) return 'partial'
  if (statusLower.includes('refund')) return 'refunded'
  return 'pending'
}

function mapPaymentMethod(method?: string): Order['payment_method'] {
  if (!method) return undefined
  
  const methodLower = method.toLowerCase()
  if (methodLower.includes('cash') || methodLower.includes('كاش')) return 'cash'
  if (methodLower.includes('card') || methodLower.includes('شبكة') || methodLower.includes('network')) return 'card'
  if (methodLower.includes('online')) return 'online'
  if (methodLower.includes('partial')) return 'partial'
  if (methodLower.includes('wallet') || methodLower.includes('محفظة')) return 'wallet'
  return undefined
}

/**
 * Fetch cashier orders list with pagination support
 */
export async function fetchCashierOrders(
  params: CashierOrdersParams = {},
  append: boolean = false
): Promise<CashierOrdersResponse> {
  try {
    // Get deviceId from localStorage or use default
    const deviceId = localStorage.getItem('deviceId') || '1'
    
    // Get language from settings or localStorage
    const language = localStorage.getItem('language') || 
      (localStorage.getItem('app_settings') ? JSON.parse(localStorage.getItem('app_settings')!).language : 'ar') || 
      'ar'

    const queryParams: Record<string, any> = {}
    if (params.typeId) queryParams.typeId = params.typeId
    // Axios serializes arrays as `typesArr[]=2&typesArr[]=3`
    if (params.typesArr?.length) queryParams.typesArr = params.typesArr
    if (params.statusesArr?.length) queryParams.statusesArr = params.statusesArr
    if (params.branchId) queryParams.branchId = params.branchId
    if (params.paymentMethod) queryParams.paymentMethod = params.paymentMethod
    if (params.platform) queryParams.platform = params.platform
    if (params.status) queryParams.status = params.status
    if (params.statusId) queryParams.statusId = params.statusId
    if (params.dateFrom) queryParams.dateFrom = params.dateFrom
    if (params.dateTo) queryParams.dateTo = params.dateTo
    if (params.page) queryParams.page = params.page
    if (params.search) queryParams.search = params.search

    // Use correct endpoint based on user type
    // For CS, use /customerService/customer-orders
    // For cashier, use /cashier/cashier-orders
    let endpoint: string
    if (isCustomerServiceUser()) {
      endpoint = '/customerService/customer-orders'
    } else {
      endpoint = buildEndpoint('/cashier/cashier-orders')
    }

    const response = await apiService.get(
      endpoint,
      queryParams,
      {
        headers: {
          'deviceId': deviceId,
        },
      }
    )

    // Handle API response structure
    let ordersData: CashierOrderResponse[] = []
    let meta = {
      total: 0,
      perPage: 25,
      currentPage: 1,
      lastPage: 1,
    }
    
    if (response.data) {
      if (Array.isArray(response.data)) {
        ordersData = response.data
      } else if (response.data.data) {
        // Handle nested data structure: data.data.collection or data.data (array)
        if (response.data.data.collection && Array.isArray(response.data.data.collection)) {
          ordersData = response.data.data.collection
          // Extract meta if available
          if (response.data.data.meta) {
            meta = {
              total: response.data.data.meta.total || 0,
              perPage: response.data.data.meta.perPage || 25,
              currentPage: response.data.data.meta.currentPage || 1,
              lastPage: response.data.data.meta.lastPage || 1,
            }
          }
        } else if (Array.isArray(response.data.data)) {
          ordersData = response.data.data
        }
      } else if (response.data.orders && Array.isArray(response.data.orders)) {
        ordersData = response.data.orders
      } else if (response.data.collection && Array.isArray(response.data.collection)) {
        ordersData = response.data.collection
        // Extract meta if available
        if (response.data.meta) {
          meta = {
            total: response.data.meta.total || 0,
            perPage: response.data.meta.perPage || 25,
            currentPage: response.data.meta.currentPage || 1,
            lastPage: response.data.meta.lastPage || 1,
          }
        }
      }
    }

    // Map each order to Order type
    const mappedOrders = ordersData.map(mapOrderResponse)
    
    return {
      orders: mappedOrders,
      meta,
    }
  } catch (error) {
    throw error
  }
}

/**
 * Fetch order details by ID - uses /cashier/orders/{orderId} endpoint
 * Returns Order with partial_payments and remainingPaymentAmount
 */
export async function fetchOrderDetails(orderId: string | number): Promise<Order & { 
  partial_payments?: any[]
  partialPayments?: any[]
  remainingPaymentAmount?: number
  remaining_payment_amount?: number
  remaining_amount?: number
}> {
  try {
    const response = await apiService.get(buildEndpoint(`/cashier/orders/${orderId}`))

    // Handle API response structure
    let orderData: CashierOrderResponse
    let rawData: any = null
    if (response.data) {
      if (response.data.data) {
        orderData = response.data.data
        rawData = response.data.data
      } else {
        orderData = response.data
        rawData = response.data
      }
    } else {
      throw new Error('Invalid response structure')
    }

    // Extract partial payments and remaining amount BEFORE mapping (to preserve original data)
    const partialPayments = rawData?.partialPayments || 
                            rawData?.partial_payments || 
                            rawData?.payments || 
                            rawData?.payment_history ||
                            []
    
    const remainingPaymentAmount = rawData?.remainingPaymentAmount || 
                                   rawData?.remaining_payment_amount || 
                                   rawData?.remaining_amount ||
                                   undefined

    const mappedOrder = mapOrderResponse(orderData)

    return {
      ...mappedOrder,
      partial_payments: Array.isArray(partialPayments) ? partialPayments : [],
      partialPayments: Array.isArray(partialPayments) ? partialPayments : [],
      remainingPaymentAmount: typeof remainingPaymentAmount === 'number' ? remainingPaymentAmount : 
                              (typeof remainingPaymentAmount === 'string' ? parseFloat(remainingPaymentAmount) : undefined),
      remaining_payment_amount: typeof remainingPaymentAmount === 'number' ? remainingPaymentAmount : 
                                (typeof remainingPaymentAmount === 'string' ? parseFloat(remainingPaymentAmount) : undefined),
      remaining_amount: typeof remainingPaymentAmount === 'number' ? remainingPaymentAmount : 
                        (typeof remainingPaymentAmount === 'string' ? parseFloat(remainingPaymentAmount) : undefined),
    }
  } catch (error) {
    throw error
  }
}

/**
 * Fetch full order details with all nested data - returns raw response
 */
export async function fetchFullOrderDetails(orderId: string | number): Promise<any> {
  try {
    const response = await apiService.get(buildEndpoint(`/cashier/orders/${orderId}`))

    // Return full response for access to all fields
    if (response.data) {
      if (response.data.data) {
        return response.data.data
      } else {
        return response.data
      }
    } else {
      throw new Error('Invalid response structure')
    }
  } catch (error) {
    throw error
  }
}

/**
 * Add partial payment to an order
 */
export async function addPartialPayment(
  orderId: string | number,
  paymentData: {
    amount: number
    paymentMethod: 'cash' | 'network'
    isSuccess: number
    transactionId?: string
  }
): Promise<any> {
  try {
    const response = await apiService.post(
      buildEndpoint(`/cashier/orders/${orderId}/add-partial-payment`),
      {
        amount: paymentData.amount,
        paymentMethod: paymentData.paymentMethod,
        isSuccess: paymentData.isSuccess,
        transaction_id: paymentData.transactionId
      }
    )

    // Handle API response structure
    if (response.data) {
      if (response.data.data) {
        return response.data.data
      } else {
        return response.data
      }
    }
    
    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * Change payment method for an order
 * PUT /cashier/orders/{orderId}/payment-method
 */
export async function changePaymentMethod(
  orderId: string | number,
  paymentMethod: string
): Promise<any> {
  try {
    const response = await apiService.put(
      buildEndpoint(`/cashier/orders/${orderId}/payment-method`),
      {
        paymentMethod: paymentMethod
      }
    )

    // Handle API response structure
    if (response.data) {
      if (response.data.data) {
        return response.data.data
      } else {
        return response.data
      }
    }
    
    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * Confirm payment for an order
 * PUT /cashier/orders/{orderId}/payment-status
 */
export async function confirmPayment(
  orderId: string | number
): Promise<any> {
  try {
    const response = await apiService.put(
      buildEndpoint(`/cashier/orders/${orderId}/payment-status`),
      {}
    )

    // Handle API response structure
    if (response.data) {
      if (response.data.data) {
        return response.data.data
      } else {
        return response.data
      }
    }
    
    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * Add items to an existing order
 * POST /{employeeType}/orders/{orderId}/items
 * Body: { items: [{ itemId, quantity, meals: [{ mealId, additionals: [{ id }] }] }] }
 */
export async function addOrderItems(
  orderId: string | number,
  items: Array<{
    itemId: number
    quantity: number
    meals: Array<{
      mealId: number
      additionals: Array<{ id: number }>
    }>
  }>
): Promise<any> {
  try {
    const response = await apiService.post(buildEndpoint(`/cashier/orders/${orderId}/items`), {
      items,
    })

    if (response.data) {
      if (response.data.data) {
        return response.data.data
      }
      return response.data
    }
    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * Remove items (or a quantity) from an existing order
 * DELETE /{employeeType}/orders/{orderId}/items/{itemId}
 * Body: { quantity } - the quantity being removed from the current item quantity
 */
export async function removeOrderItem(
  orderId: string | number,
  itemId: string | number,
  quantity: number
): Promise<any> {
  try {
    const response = await apiService.delete(buildEndpoint(`/cashier/orders/${orderId}/items/${itemId}`), {
      quantity,
    })

    if (response.data) {
      if (response.data.data) {
        return response.data.data
      }
      return response.data
    }
    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * Cancel an order
 * POST /cashier/orders/{orderId}/cancel
 */
export async function cancelOrder(
  orderId: string | number
): Promise<any> {
  try {
    const response = await apiService.post(
      buildEndpoint(`/cashier/orders/${orderId}/cancel`),
      {}
    )

    // Handle API response structure
    if (response.data) {
      if (response.data.data) {
        return response.data.data
      } else {
        return response.data
      }
    }
    
    return response.data
  } catch (error) {
    throw error
  }
}

